import { useCallback, useMemo } from 'react';

import { useMentionSearchQuery } from '@/modules/corporate/modules/channel/views/ChannelDetails/graphql/queries/generated/mentionSearch';
import { ContentEntity, EntityTypeEnum, EventUnion, UserType } from '@/types/schema';

import {
  ALLOWED_CONTENT_STATUS,
  ALLOWED_CONTENT_TYPES,
  ALLOWED_EVENT_STATUS,
  ALLOWED_USER_STATUS,
} from '../../consts';

const useMentions = ({ searchText, pause }: { searchText: string; pause?: boolean }) => {
  const [{ data }] = useMentionSearchQuery({
    variables: {
      filters: {
        searchText,
        types: [EntityTypeEnum.Resource, EntityTypeEnum.User, EntityTypeEnum.Event],
      },
      contentFilters: {
        types: ALLOWED_CONTENT_TYPES,
        status: ALLOWED_CONTENT_STATUS,
      },
      usersFilters: { status: ALLOWED_USER_STATUS },
      eventFilters: {
        status: ALLOWED_EVENT_STATUS,
      },
      first: 7,
    },
    pause,
  });

  const getData = useCallback(
    <DataType>(key: 'users' | 'contents' | 'events') => {
      return {
        data: (data?.mentionSearch?.[key]?.edges?.map(({ node }) => node) ||
          []) as unknown as DataType[],
        dataCount: data?.mentionSearch?.[key]?.totalCount || 0,
      };
    },
    [data?.mentionSearch],
  );

  const { data: users, dataCount: usersCount } = useMemo(
    () => getData<UserType>('users'),
    [getData],
  );
  const { data: events, dataCount: eventsCount } = useMemo(
    () => getData<EventUnion>('events'),
    [getData],
  );
  const { data: contents, dataCount: contentsCount } = useMemo(
    () => getData<ContentEntity>('contents'),
    [getData],
  );

  return { users, contents, events, totalCount: usersCount + eventsCount + contentsCount };
};

export default useMentions;
